import { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { type DocumentQueryView, type DocumentRowView } from '@vello/api-client';
import { formatMarketInstant, marketTimeZone } from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { documentTypeName } from '../i18n/document-type';
import { MarketField } from '../components/market-field';
import { usePhoneText } from '../components/phone-text';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, Field, Notice, PageHeader, Panel, SegmentedControl, Select, Table, TextInput, FormRow, EmptyState, Text, Stack, PanelBody, Row } from '@vello/web-theme';
const PAGE_SIZE = 25;
const STATUSES = ['', 'SUBMITTED', 'PROCESSING', 'REVIEW_REQUIRED', 'APPROVED', 'REJECTED', 'EXPIRED'] as const;

const tone = (status: string): 'neutral' | 'success' | 'warning' | 'danger' =>
  status === 'APPROVED' ? 'success' : status === 'REJECTED' || status === 'EXPIRED' ? 'danger' : status === 'NOT_SUBMITTED' ? 'neutral' : 'warning';

/**
 * **Motoristas › Consulta de documentos** (W9-DRIVER-REGISTRY, D481, ADR-0099; DevMobility D-025 "Consulta centralizada de
 * documentos", D-026 "Fila de documentos em aprovação") over `GET /v1/admin/driver-documents` (`document:view`, GEO).
 *
 * - Two views of the market's documents of drivers AND their vehicles: **Em aprovação** (the lines waiting for a decision) and
 *   **Todos** (the newest line of each document, or every line with "histórico"). Filter by status, document type and an
 *   expiry watch ("vence até"). The document number is always masked; a file is only said to exist, never linked here.
 * - "Abrir motorista" goes to the driver's record; the in-person review itself stays in Motoristas › Documentos (one
 *   reviewer line per type, `document:review`). The market comes from the operator's scope. A refused read is an error,
 *   never an empty list. No native dialog is ever opened.
 */
export function DriverDocumentQueueScreen(): ReactElement {
  const { runtime, markets } = useAdmin();
  const phoneOf = usePhoneText();
  const { t, lang } = useT();
  const { v, refusal: refusalOf } = useVocab();
  const { navigate } = useRouter();
  const [market, setMarket] = useState('');
  const [mode, setMode] = useState<'queue' | 'all'>('queue');
  const [status, setStatus] = useState('');
  const [type, setType] = useState('');
  const [until, setUntil] = useState('');
  const [history, setHistory] = useState(false);
  const [page, setPage] = useState(0);
  const [loaded, setLoaded] = useState<string | undefined>(undefined);
  const [view, setView] = useState<DocumentQueryView | undefined>(undefined);
  const [error, setError] = useState<string | undefined>(undefined);
  /** The catalog types last read: kept across re-reads so the type filter never loses its options while the grid loads. */
  const [typeOptions, setTypeOptions] = useState<DocumentQueryView['types']>([]);
  const seq = useRef(0);

  const load = useCallback(
    async (m: string, p: number, queue: boolean, background = false): Promise<void> => {
      const n = ++seq.current;
      // A background tick keeps the rows on screen; a new market / mode / filter / page starts from a clean grid.
      if (!background) {
        setError(undefined);
        setLoaded(m);
        setView(undefined);
      }
      if (m === '') return;
      const res = await runtime.driverRegistry.documents({
        marketId: m,
        ...(queue ? { queue: true } : status !== '' ? { status } : {}),
        ...(type !== '' ? { type } : {}),
        ...(until !== '' ? { expiresBefore: `${until}T23:59:59.999Z` } : {}),
        ...(!queue && history ? { history: true } : {}),
        limit: PAGE_SIZE,
        offset: p * PAGE_SIZE,
      });
      if (n !== seq.current) return;
      if (res.ok) {
        setError(undefined);
        setView(res.value);
        setTypeOptions(res.value.types);
      } else setError(refusalOf('refusal.admin.driver_registry', res.error) ?? t('registry.refusal.UNKNOWN'));
    },
    [runtime, status, type, until, history, t],
  );

  // The grid follows the scoped market, the mode and the filters by itself, and stays current in the background.
  useEffect(() => {
    setPage(0);
    void load(market, 0, mode === 'queue');
  }, [market, mode, load]);
  useAutoRefresh(() => load(market, page, mode === 'queue', true), { intervalMs: AUTO_REFRESH_MS.queue, enabled: market !== '' });

  const tz = marketTimeZone(loaded, markets);
  const typeLabel = (code: string): string => {
    return documentTypeName(view?.types ?? typeOptions, code, lang, v);
  };
  const total = view?.total ?? 0;
  const pages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  return (
    <>
      <PageHeader title={t('registry.docs.title')} subtitle={t('registry.docs.subtitle')} />

      <Panel><Stack gap={4}>
        <div>
          <SegmentedControl
            ariaLabel={t('registry.docs.mode')}
            value={mode}
            onChange={(v: 'queue' | 'all') => {
              setMode(v);
            }}
            options={[
              { id: 'queue', label: t('registry.docs.mode.queue') },
              { id: 'all', label: t('registry.docs.mode.all') },
            ]}
          />
        </div>
        <form onSubmit={(e) => e.preventDefault()}>
          <FormRow action={mode === 'all' ? <CheckRow id="docq-history" checked={history} onChange={setHistory}>{t('registry.docs.history')}</CheckRow> : undefined}>
            <MarketField id="docq-market" value={market} onChange={setMarket} />
            {mode === 'all' && (
              <Field label={t('registry.docs.status')} htmlFor="docq-status">
                <Select id="docq-status" value={status} onChange={(e) => setStatus(e.target.value)}>
                  {STATUSES.map((s) => (
                    <option key={s} value={s}>{s === '' ? t('registry.docs.status.all') : v('kyc.document_status', s)}</option>
                  ))}
                </Select>
              </Field>
            )}
            <Field label={t('registry.docs.type')} htmlFor="docq-type">
              <Select id="docq-type" value={type} onChange={(e) => setType(e.target.value)}>
                <option value="">{t('registry.docs.type.all')}</option>
                {typeOptions.filter((x) => x.active).map((x) => (
                  <option key={x.code} value={x.code}>{typeLabel(x.code)}</option>
                ))}
              </Select>
            </Field>
            <Field label={t('registry.docs.until')} htmlFor="docq-until" hint={t('registry.docs.until.hint')}>
              <TextInput id="docq-until" className="tnum" type="date" value={until} onChange={(e) => setUntil(e.target.value)} />
            </Field>
          </FormRow>
        </form>
        {loaded === '' && <Notice tone="neutral">{t('registry.marketHint')}</Notice>}
        {error !== undefined && <Notice tone="danger">{error}</Notice>}
      </Stack></Panel>

      <section aria-label={t('registry.docs.list')}>
        <Panel padded={false}>
          <Table
            columns={[
              {
                key: 'driver',
                header: t('registry.docs.col.driver'),
                render: (d: DocumentRowView) => (
                  <span>
                    {d.driverName ?? '—'}
                    {d.driverPhoneLast4 !== null && <Text role="caption" tone="tertiary" mono block>{phoneOf(`•••• ${d.driverPhoneLast4}`)}</Text>}
                  </span>
                ),
              },
              {
                key: 'type',
                header: t('registry.docs.col.type'),
                render: (d: DocumentRowView) => (
                  <span>
                    {typeLabel(d.type)}
                    <Text role="caption" tone="tertiary" block>{v('kyc.document_owner_type', d.ownerType)}</Text>
                  </span>
                ),
              },
              { key: 'number', header: t('registry.docs.col.number'), render: (d: DocumentRowView) => <span className="tnum">{d.numberMasked}</span> },
              {
                key: 'status',
                header: t('registry.docs.col.status'),
                render: (d: DocumentRowView) => (
                  <span>
                    <Badge tone={tone(d.status)}>{v('kyc.document_status', d.status)}</Badge>
                    {d.reviewMethod !== undefined && <Text role="caption" tone="tertiary" block>{v('kyc.review_method', d.reviewMethod)}</Text>}
                  </span>
                ),
              },
              { key: 'expires', header: t('registry.docs.col.expires'), render: (d: DocumentRowView) => <span className="tnum">{formatMarketInstant(d.expiresAt, tz, lang, 'date')}</span> },
              { key: 'updated', header: t('registry.docs.col.updated'), render: (d: DocumentRowView) => <span className="tnum">{formatMarketInstant(d.updatedAt, tz, lang)}</span> },
            ]}
            rows={view?.documents ?? []}
            rowKey={(d) => d.documentId}
            onRowClick={(d) => { if (d.driverId !== null) navigate('driver_detail', { id: d.driverId }); }}
            loading={loaded !== undefined && loaded !== '' && view === undefined && error === undefined}
            loadingLabel={t('ui.loading')}
            error={error !== undefined ? t('ui.loadFailed') : undefined}
            onRetry={() => void load(market, page, mode === 'queue')}
            retryLabel={t('ui.retry')}
            empty={<EmptyState title={loaded === undefined || loaded === '' ? t('registry.marketHint') : t(mode === 'queue' ? 'registry.docs.empty.queue' : 'registry.docs.empty')} />}
          />
          {view !== undefined && total > PAGE_SIZE && (
            <PanelBody><Row gap={2} align="center">
              <Button size="sm" variant="ghost" disabled={page === 0} onClick={() => { setPage(page - 1); void load(market, page - 1, mode === 'queue'); }}>{t('registry.page.prev')}</Button>
              <Text role="caption" tone="tertiary" block>{t('registry.page.of', { page: String(page + 1), pages: String(pages), total: String(total) })}</Text>
              <Button size="sm" variant="ghost" disabled={page + 1 >= pages} onClick={() => { setPage(page + 1); void load(market, page + 1, mode === 'queue'); }}>{t('registry.page.next')}</Button>
            </Row></PanelBody>
          )}
        </Panel>
      </section>
    </>
  );
}
